/**
 * ModelProp design tokens, exactly as specified in the design handoff.
 *
 * Literal values only — the handoff states every value is a literal hex/px in
 * the source so it can be read off any element. Do not introduce derived or
 * approximated colours here.
 */

export const c = {
  indigo: '#094074',
  indigo900: '#051f3a',
  lapis: '#3C6997',
  sky: '#5ADBFF',
  orange: '#FE9000',
  orangeHover: '#FFA526',
  orange700: '#C66F00',
  orange100: '#FFEACC',
  mustard: '#FFDD4A',
  ink: '#0B1220',
  ink2: '#2B3441',
  ink3: '#5A6573',
  line: '#E4E8EE',
  lineSoft: '#F0F3F7',
  surface: '#FAFBFC',
  surface2: '#FFFFFF',
  success: '#1F8A5B',
  danger: '#D7263D',
  neutralBar: '#D8DEE7',
  indigo100: '#D6E4F1',
  /*
   * ModelProp product family (17 Oct 2026): every Prop shares the tokens above and has one
   * colour of its own. VoiceProp's is gold. productInk is the darker shade for words on
   * white. Orange stays as the "needs attention" colour and ModelProp's own house.
   */
  product: '#F2B705',
  productInk: '#8A6400',
  productTint: '#FDF2CC',
} as const;

export const railGradient = 'linear-gradient(180deg, #051f3a 0%, #094074 100%)';

export const lockedGradient =
  'linear-gradient(120deg, rgba(9,64,116,0.035) 0%, rgba(90,219,255,0.05) 100%)';

/** Rail text opacities on the gradient. */
export const rail = {
  primary: 'rgba(255,255,255,0.80)',
  sectionLabel: 'rgba(255,255,255,0.42)',
  meta: 'rgba(255,255,255,0.46)',
  metaLocked: 'rgba(255,255,255,0.26)',
  locked: 'rgba(255,255,255,0.34)',
  divider: 'rgba(255,255,255,0.10)',
  hoverFill: 'rgba(255,255,255,0.06)',
  dotIdle: 'rgba(255,255,255,0.18)',
  userRole: 'rgba(255,255,255,0.50)',
  duty: 'rgba(255,255,255,0.62)',
} as const;

export const shadow = {
  card: '0 1px 2px rgba(9,64,116,0.06)',
  cardHover: '0 6px 20px rgba(9,64,116,0.08)',
  drawer: '0 24px 60px rgba(9,64,116,0.12)',
  buttonHover: '0 6px 20px rgba(9,64,116,0.14)',
} as const;

export const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
export const motion = {
  micro: `180ms ${ease}`,
  default: `300ms ${ease}`,
} as const;

export const radius = {
  nav: 8,
  button: 10,
  list: 12,
  card: 16,
  pill: 9999,
} as const;

export const border = {
  default: `1px solid ${c.line}`,
  emphasis: '1px solid rgba(9,64,116,0.18)',
  soft: `1px solid ${c.lineSoft}`,
} as const;

export const mono = "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace";
export const sans = "'Questrial', ui-sans-serif, system-ui, sans-serif";

/** Outcome pill colours, keyed on calls.outcome. */
export const categoryStyle: Record<string, { bg: string; fg: string }> = {
  viewing_request: { bg: 'rgba(9,64,116,0.07)', fg: c.indigo },
  message: { bg: c.lineSoft, fg: c.ink2 },
  booking_confirmed: { bg: 'rgba(31,138,91,0.10)', fg: c.success },
  transferred: { bg: 'rgba(60,105,151,0.10)', fg: c.lapis },
  valuation: { bg: 'rgba(254,144,0,0.12)', fg: c.orange700 },
  complaint: { bg: 'rgba(215,38,61,0.08)', fg: c.danger },
  abandoned: { bg: 'rgba(215,38,61,0.08)', fg: c.danger },
  no_outcome: { bg: c.lineSoft, fg: c.ink3 },
};

export function categoryFor(outcome: string | null): { bg: string; fg: string } {
  return categoryStyle[outcome ?? 'no_outcome'] ?? categoryStyle.no_outcome!;
}

export const sentimentColour = {
  positive: c.success,
  neutral: c.neutralBar,
  frustrated: c.danger,
} as const;

export const severityColour = {
  viewing: c.indigo,
  urgent: c.danger,
  lead: c.orange,
} as const;
